import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Calendar, DollarSign, RefreshCw, Plus, Search, Edit2, Trash2, X, AlertCircle, ArrowUpDown, ArrowUp, ArrowDown, Inbox, FileText, Coins, List, LayoutGrid, PlayCircle, StopCircle, RotateCcw, Clock, Info, Link2, ChevronLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { isDepartmentManager } from '../utils/roleHelpers';
import TablePagination from '../components/TablePagination';

const SUB_PAGE_SIZE = 25;

// ── Financial Year Helpers ──────────────────────────────────────────
const _now = new Date();
const DEFAULT_FY_YEAR = _now.getMonth() >= 3 ? _now.getFullYear() : _now.getFullYear() - 1;
const FY_YEARS = Array.from({ length: 5 }, (_, i) => DEFAULT_FY_YEAR - i + 1);

function generateFYMonths(fyStartYear) {
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(fyStartYear, 3 + i, 1); // April=3
    return {
      year: d.getFullYear(),
      month: d.getMonth(),
      label: d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
      fullLabel: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
      start: new Date(d.getFullYear(), d.getMonth(), 1),
      end: new Date(d.getFullYear(), d.getMonth() + 1, 0),
    };
  });
}

function isActiveInMonth(sub, m) {
  const start = new Date(sub.StartDate);
  const end = sub.EndDate ? new Date(sub.EndDate) : null;
  return start <= m.end && (!end || end >= m.start);
}

function getMonthMarkers(sub, m) {
  const start = new Date(sub.StartDate);
  const end = sub.EndDate ? new Date(sub.EndDate) : null;
  const renewal = sub.RenewalDate ? new Date(sub.RenewalDate) : null;
  return {
    isStart: start.getFullYear() === m.year && start.getMonth() === m.month,
    isEnd: end && end.getFullYear() === m.year && end.getMonth() === m.month,
    isRenewal: renewal && renewal.getFullYear() === m.year && renewal.getMonth() === m.month,
  };
}

function formatAmt(n) {
  if (!n && n !== 0) return '—';
  return '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// Compute the "Review By" date (when you need to decide about renewal)
// based on renewal date minus notice period
function computeReviewByDate(renewalDate, noticePeriodDays, noticePeriodUnit) {
  if (!renewalDate || !noticePeriodDays) return null;
  const days = parseInt(noticePeriodDays, 10);
  if (isNaN(days) || days <= 0) return null;
  const d = new Date(renewalDate);
  if (isNaN(d.getTime())) return null;
  if (noticePeriodUnit === 'Months') d.setMonth(d.getMonth() - days);
  else if (noticePeriodUnit === 'Weeks') d.setDate(d.getDate() - days * 7);
  else d.setDate(d.getDate() - days); // Days
  return d;
}

function getReviewStatus(reviewByDate) {
  if (!reviewByDate) return null;
  const now = new Date();
  const diffMs = reviewByDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: `${Math.abs(diffDays)}d overdue`, color: 'bg-red-100 text-red-800 border-red-200', icon: 'red' };
  if (diffDays <= 30) return { label: `${diffDays}d`, color: 'bg-red-100 text-red-800 border-red-200', icon: 'red' };
  if (diffDays <= 60) return { label: `${diffDays}d`, color: 'bg-amber-100 text-amber-800 border-amber-200', icon: 'amber' };
  return { label: reviewByDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }), color: 'bg-green-50 text-green-700 border-green-200', icon: 'green' };
}

export default function Subscriptions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isDeptMgr = isDepartmentManager(user);

  const [subscriptions, setSubscriptions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [glAccounts, setGLAccounts] = useState([]);
  const [formGLAccounts, setFormGLAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Active');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sortField, setSortField] = useState('ContractName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [viewMode, setViewMode] = useState('list');
  const [fyYear, setFyYear] = useState(DEFAULT_FY_YEAR);
  const [subPage, setSubPage] = useState(1);
  const timelineRef = useRef(null);

  // Modal tabs (Details / Linked Invoices — invoices tab only shown when editing)
  const [activeTab, setActiveTab] = useState('details');
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const [linkedInvoicesLoading, setLinkedInvoicesLoading] = useState(false);

  const [formData, setFormData] = useState({
    vendorId: '',
    departmentId: '',
    contractName: '',
    description: '',
    contractNumber: '',
    paymentFrequency: 'Monthly',
    paymentAmount: '',
    paymentDayOfMonth: '',
    currency: 'GBP',
    startDate: '',
    endDate: '',
    renewalDate: '',
    autoRenew: true,
    glAccountId: '',
    status: 'Active',
    noticePeriodDays: '',
    noticePeriodUnit: 'Months'
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterDepartment]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterDepartment) params.append('departmentId', filterDepartment);

      const [subsResponse, vendorsResponse, deptsResponse, glResponse] = await Promise.all([
        apiClient.get(`/subscriptions?${params.toString()}`),
        apiClient.get('/vendors'),
        apiClient.get('/departments'),
        apiClient.get('/gl-accounts/active')
      ]);

      const subsData = subsResponse.data.subscriptions || subsResponse.data;
      const vendorsData = vendorsResponse.data.vendors || vendorsResponse.data;
      const glData = glResponse.data.glAccounts || glResponse.data;
      setSubscriptions(Array.isArray(subsData) ? subsData : []);
      setVendors(Array.isArray(vendorsData) ? vendorsData : []);
      setDepartments(Array.isArray(deptsResponse.data?.departments) ? deptsResponse.data.departments : []);
      const glList = Array.isArray(glData) ? glData : [];
      setGLAccounts(glList);
      setFormGLAccounts(glList);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Ensure arrays are set even on error
      setSubscriptions([]);
      setVendors([]);
      setDepartments([]);
      setGLAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeptGLAccounts = async (deptId) => {
    try {
      const url = deptId ? `/gl-accounts/active?departmentId=${deptId}` : '/gl-accounts/active';
      const res = await apiClient.get(url);
      const data = res.data.glAccounts || res.data || [];
      setFormGLAccounts(Array.isArray(data) ? data : glAccounts);
    } catch {
      setFormGLAccounts(glAccounts);
    }
  };

  const handleDepartmentChange = (deptId) => {
    setFormData(prev => ({ ...prev, departmentId: deptId, glAccountId: '' }));
    fetchDeptGLAccounts(deptId);
  };

  const resetForm = () => {
    setFormData({
      vendorId: '',
      departmentId: '',
      contractName: '',
      description: '',
      contractNumber: '',
      paymentFrequency: 'Monthly',
      paymentAmount: '',
      paymentDayOfMonth: '',
      currency: 'GBP',
      startDate: '',
      endDate: '',
      renewalDate: '',
      autoRenew: true,
      glAccountId: '',
      status: 'Active',
      noticePeriodDays: '',
      noticePeriodUnit: 'Months'
    });
    setFormGLAccounts(glAccounts);
    setEditingId(null);
    setActiveTab('details');
    setLinkedInvoices([]);
  };

  const handleEdit = (subscription) => {
    const deptId = subscription.DepartmentID || '';
    setFormData({
      vendorId: subscription.VendorID || '',
      departmentId: deptId,
      contractName: subscription.ContractName || '',
      description: subscription.Description || '',
      contractNumber: subscription.ContractNumber || '',
      paymentFrequency: subscription.PaymentFrequency || 'Monthly',
      paymentAmount: subscription.PaymentAmount || '',
      paymentDayOfMonth: subscription.PaymentDayOfMonth || '',
      currency: subscription.Currency || 'GBP',
      startDate: subscription.StartDate?.split('T')[0] || '',
      endDate: subscription.EndDate?.split('T')[0] || '',
      renewalDate: subscription.RenewalDate?.split('T')[0] || '',
      autoRenew: subscription.AutoRenew || false,
      glAccountId: subscription.GLAccountID || '',
      status: subscription.Status || 'Active',
      noticePeriodDays: subscription.NoticePeriodDays ?? '',
      noticePeriodUnit: subscription.NoticePeriodUnit || 'Months'
    });
    fetchDeptGLAccounts(deptId);
    setEditingId(subscription.SubscriptionID);
    setActiveTab('details');
    setLinkedInvoices([]);
    setShowModal(true);
  };

  // Fetch linked invoices when editing a subscription
  useEffect(() => {
    if (!editingId) return;
    setLinkedInvoicesLoading(true);
    apiClient.get(`/subscriptions/${editingId}/invoices`)
      .then(res => setLinkedInvoices(res.data.invoices || []))
      .catch(() => setLinkedInvoices([]))
      .finally(() => setLinkedInvoicesLoading(false));
  }, [editingId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await apiClient.put(`/subscriptions/${editingId}`, formData);
      } else {
        await apiClient.post('/subscriptions', formData);
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save subscription:', error);
      alert('Failed to save subscription');
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Cancel subscription: ${name}?`)) return;
    
    try {
      await apiClient.delete(`/subscriptions/${id}`);
      fetchData();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      alert('Failed to cancel subscription');
    }
  };

  const handleSort = useCallback((field) => {
    setSortDirection(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  }, [sortField]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-400" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  const filteredSubscriptions = useMemo(() => {
    let result = subscriptions.filter(sub =>
      sub.ContractName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.VendorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.Description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (sortField === 'PaymentAmount') {
        aVal = parseFloat(aVal) || 0; bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'RenewalDate') {
        aVal = new Date(aVal || 0).getTime(); bVal = new Date(bVal || 0).getTime();
      } else {
        aVal = String(aVal || '').toLowerCase(); bVal = String(bVal || '').toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [subscriptions, searchTerm, sortField, sortDirection]);

  const pagedSubscriptions = useMemo(
    () => filteredSubscriptions.slice((subPage - 1) * SUB_PAGE_SIZE, subPage * SUB_PAGE_SIZE),
    [filteredSubscriptions, subPage]
  );
  // Reset page on filter/sort changes
  useEffect(() => { setSubPage(1); }, [searchTerm, filterStatus, filterDepartment, sortField, sortDirection]);

  const totalMonthlyRecurring = subscriptions
    .filter(s => s.Status === 'Active' && s.PaymentFrequency === 'Monthly')
    .reduce((sum, s) => sum + (s.PaymentAmount || 0), 0);

  const totalAnnualRecurring = subscriptions
    .filter(s => s.Status === 'Active' && s.PaymentFrequency === 'Annually')
    .reduce((sum, s) => sum + (s.PaymentAmount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">Loading subscriptions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back navigation */}
      {location.state?.from && (
        <button
          onClick={() => navigate(location.state.from)}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium group"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to {location.state.fromLabel || 'Previous Page'}
        </button>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-blue-600" />
            Subscriptions & Contracts
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage recurring contracts with automatic budget integration</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-2 font-medium transition-colors ${
                viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title="List view"
            >
              <List className="w-4 h-4" /> List
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={`flex items-center gap-1.5 px-3 py-2 font-medium transition-colors ${
                viewMode === 'timeline' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title="Timeline view"
            >
              <LayoutGrid className="w-4 h-4" /> Timeline
            </button>
          </div>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Subscription
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Monthly Recurring</p>
              <p className="text-2xl font-bold text-gray-900">£{totalMonthlyRecurring.toLocaleString()}</p>
            </div>
            <Coins className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Annual Recurring</p>
              <p className="text-2xl font-bold text-gray-900">£{totalAnnualRecurring.toLocaleString()}</p>
            </div>
            <Calendar className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Subscriptions</p>
              <p className="text-2xl font-bold text-gray-900">
                {subscriptions.filter(s => s.Status === 'Active').length}
              </p>
            </div>
            <RefreshCw className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search subscriptions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Expired">Expired</option>
          <option value="Cancelled">Cancelled</option>
        </select>
        {!isDeptMgr && (
        <select
          value={filterDepartment}
          onChange={(e) => setFilterDepartment(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All Departments</option>
          {departments.map(dept => (
            <option key={dept.DepartmentID} value={dept.DepartmentID}>
              {dept.DepartmentName || dept.Name}
            </option>
          ))}
        </select>
        )}
        {searchTerm && (
          <span className="text-sm text-gray-500 self-center">
            {filteredSubscriptions.length} of {subscriptions.length}
          </span>
        )}
      </div>

      {/* Subscriptions Table */}
      {viewMode === 'list' && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('ContractName')}>
                  <span className="inline-flex items-center">Contract <SortIcon field="ContractName" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('VendorName')}>
                  <span className="inline-flex items-center">Vendor <SortIcon field="VendorName" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('DepartmentName')}>
                  <span className="inline-flex items-center">Department <SortIcon field="DepartmentName" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('PaymentFrequency')}>
                  <span className="inline-flex items-center">Frequency <SortIcon field="PaymentFrequency" /></span>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('PaymentAmount')}>
                  <span className="inline-flex items-center justify-end">Amount <SortIcon field="PaymentAmount" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('RenewalDate')}>
                  <span className="inline-flex items-center">Renewal <SortIcon field="RenewalDate" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Review By
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Auto-Renew</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('Status')}>
                  <span className="inline-flex items-center">Status <SortIcon field="Status" /></span>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSubscriptions.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-12 text-center">
                    <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 font-medium">
                      {searchTerm ? 'No subscriptions match your search' : 'No subscriptions found'}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">
                      {searchTerm ? 'Try adjusting your search' : 'Click "New Subscription" to get started'}
                    </p>
                  </td>
                </tr>
              ) : (
                pagedSubscriptions.map(sub => (
                  <tr key={sub.SubscriptionID} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{sub.ContractName}</div>
                      {sub.Description && (
                        <div className="text-xs text-gray-500 max-w-[200px] truncate">{sub.Description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{sub.VendorName}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{sub.DepartmentName || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        sub.PaymentFrequency === 'Monthly'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {sub.PaymentFrequency}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                      £{(sub.PaymentAmount || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {sub.RenewalDate
                        ? new Date(sub.RenewalDate).toLocaleDateString()
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {(() => {
                        const rbd = computeReviewByDate(sub.RenewalDate, sub.NoticePeriodDays, sub.NoticePeriodUnit);
                        const status = getReviewStatus(rbd);
                        if (!status) return <span className="text-gray-300 text-xs">—</span>;
                        return (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${status.color}`}>
                            <Clock className="w-3 h-3" />
                            {status.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {sub.AutoRenew ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">ON</span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">OFF</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        sub.Status === 'Active'
                          ? 'bg-green-100 text-green-800'
                          : sub.Status === 'Expired'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {sub.Status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(sub)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(sub.SubscriptionID, sub.ContractName)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Cancel"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePagination page={subPage} pageSize={SUB_PAGE_SIZE} total={filteredSubscriptions.length} onPageChange={setSubPage} />
      </div>
      )} {/* end viewMode === 'list' */}

      {/* ── Timeline View ── */}
      {viewMode === 'timeline' && (() => {
        const fyMonths = generateFYMonths(fyYear);
        const rows = filteredSubscriptions;

        // Per-month totals
        const monthTotals = fyMonths.map(m => ({
          ...m,
          total: rows.reduce((sum, sub) => {
            if (!isActiveInMonth(sub, m)) return sum;
            const amt = sub.PaymentFrequency === 'Monthly' ? (sub.PaymentAmount || 0) : 0;
            return sum + amt;
          }, 0),
          annualInMonth: rows.reduce((sum, sub) => {
            if (sub.PaymentFrequency !== 'Annually') return sum;
            const { isStart, isRenewal } = getMonthMarkers(sub, m);
            return (isStart || isRenewal) ? sum + (sub.PaymentAmount || 0) : sum;
          }, 0),
        }));

        return (
          <div>
            {/* FY selector + legend */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-gray-700">Financial Year:</span>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
                  {FY_YEARS.map(y => (
                    <button
                      key={y}
                      onClick={() => setFyYear(y)}
                      className={`px-3 py-1.5 font-medium transition-colors ${fyYear === y ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      {y}/{String(y + 1).slice(2)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> Contract start</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> Contract end</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500 inline-block" /> Renewal due</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-orange-400 inline-block" /> Review deadline</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-100 border border-blue-300 inline-block" /> Active (monthly)</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-purple-100 border border-purple-300 inline-block" /> Annual payment</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gray-100 border border-dashed border-gray-300 inline-block" /> Inactive</span>
              </div>
            </div>

            <div ref={timelineRef} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-auto max-h-[calc(100vh-320px)]">
              <table className="min-w-max w-full border-collapse text-xs">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-gray-800 text-white">
                    <th className="sticky left-0 z-30 bg-gray-800 px-3 py-2.5 text-left font-semibold min-w-[180px]">Contract</th>
                    <th className="sticky left-[180px] z-30 bg-gray-800 px-3 py-2.5 text-left font-semibold min-w-[120px]">Vendor</th>
                    <th className="sticky left-[300px] z-30 bg-gray-800 px-3 py-2.5 text-left font-semibold min-w-[110px]">Dept</th>
                    <th className="bg-gray-700 px-2 py-2.5 text-center font-semibold min-w-[70px]">Freq</th>
                    <th className="bg-gray-700 px-2 py-2.5 text-right font-semibold min-w-[80px]">Rate</th>
                    {fyMonths.map((m, i) => (
                      <th key={i} className={`px-2 py-2.5 text-center font-semibold min-w-[80px] border-l border-gray-700 ${m.year === _now.getFullYear() && m.month === _now.getMonth() ? 'bg-blue-700' : ''}`}>
                        {m.label}
                      </th>
                    ))}
                    <th className="bg-gray-700 px-3 py-2.5 text-right font-semibold min-w-[90px] border-l border-gray-700">FY Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={5 + fyMonths.length + 1} className="py-12 text-center text-gray-400">
                        No subscriptions to display
                      </td>
                    </tr>
                  ) : rows.map((sub, ri) => {
                    // Calculate FY total for this subscription
                    let fyTotal = 0;
                    if (sub.PaymentFrequency === 'Monthly') {
                      fyTotal = fyMonths.filter(m => isActiveInMonth(sub, m)).length * (sub.PaymentAmount || 0);
                    } else {
                      // Annual: count if any payment falls in FY
                      fyTotal = fyMonths.some(m => {
                        const { isStart, isRenewal } = getMonthMarkers(sub, m);
                        return isStart || isRenewal;
                      }) ? (sub.PaymentAmount || 0) : 0;
                    }

                    const rowBg = ri % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                    return (
                      <tr key={sub.SubscriptionID} className={`border-b border-gray-100 group hover:bg-blue-50 transition-colors ${rowBg}`}>
                        {/* Sticky: Contract */}
                        <td className={`sticky left-0 z-10 px-3 py-2 group-hover:bg-blue-50 transition-colors ${rowBg}`}>
                          <div className="font-medium text-gray-900 truncate max-w-[160px]" title={sub.ContractName}>{sub.ContractName}</div>
                          {sub.ContractNumber && <div className="text-[10px] text-gray-400">{sub.ContractNumber}</div>}
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                              sub.Status === 'Active' ? 'bg-green-100 text-green-700'
                              : sub.Status === 'Expired' ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                            }`}>{sub.Status}</span>
                            {sub.AutoRenew ? <span className="text-[10px] text-blue-500 font-medium">↺ Auto</span> : null}
                          </div>
                        </td>
                        {/* Sticky: Vendor */}
                        <td className={`sticky left-[180px] z-10 px-3 py-2 text-gray-600 group-hover:bg-blue-50 transition-colors ${rowBg}`}>
                          <div className="truncate max-w-[110px]" title={sub.VendorName}>{sub.VendorName}</div>
                        </td>
                        {/* Sticky: Dept */}
                        <td className={`sticky left-[300px] z-10 px-3 py-2 text-gray-500 group-hover:bg-blue-50 transition-colors ${rowBg}`}>
                          <div className="truncate max-w-[100px]" title={sub.DepartmentName || '—'}>{sub.DepartmentName || <span className="text-gray-300">—</span>}</div>
                        </td>
                        {/* Freq */}
                        <td className="px-2 py-2 text-center">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${sub.PaymentFrequency === 'Monthly' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {sub.PaymentFrequency === 'Monthly' ? 'Mo' : 'An'}
                          </span>
                        </td>
                        {/* Rate */}
                        <td className="px-2 py-2 text-right font-mono font-semibold text-gray-700">{formatAmt(sub.PaymentAmount)}</td>

                        {/* Month cells */}
                        {fyMonths.map((m, mi) => {
                          const active = isActiveInMonth(sub, m);
                          const { isStart, isEnd, isRenewal } = getMonthMarkers(sub, m);
                          const isCurrentMonth = m.year === _now.getFullYear() && m.month === _now.getMonth();

                          // Review By Date marker
                          const reviewByDate = computeReviewByDate(sub.RenewalDate, sub.NoticePeriodDays, sub.NoticePeriodUnit);
                          const isReviewMonth = reviewByDate &&
                            reviewByDate.getFullYear() === m.year &&
                            reviewByDate.getMonth() === m.month;

                          let cellBg = 'bg-gray-50 border-dashed border-gray-200';
                          let amtDisplay = null;

                          if (active) {
                            if (sub.PaymentFrequency === 'Monthly') {
                              cellBg = 'bg-blue-50 border-blue-200';
                              amtDisplay = formatAmt(sub.PaymentAmount);
                            } else {
                              // Annual: only show amount in start/renewal month
                              if (isStart || isRenewal) {
                                cellBg = 'bg-purple-50 border-purple-200';
                                amtDisplay = formatAmt(sub.PaymentAmount);
                              } else {
                                cellBg = 'bg-purple-50/40 border-purple-100';
                              }
                            }
                          }

                          if (isStart) cellBg = 'bg-green-50 border-green-300';
                          if (isEnd) cellBg = 'bg-red-50 border-red-300';
                          if (isRenewal && !isStart && !isEnd) cellBg = 'bg-amber-50 border-amber-300';
                          if (isCurrentMonth && active) cellBg += ' ring-1 ring-inset ring-blue-400';

                          return (
                            <td key={mi} className={`px-2 py-1.5 border-l border-gray-100 ${cellBg} border`}>
                              <div className="flex flex-col items-center gap-0.5">
                                {/* Markers */}
                                <div className="flex items-center gap-0.5">
                                  {isStart && <span title="Contract starts" className="w-2 h-2 rounded-full bg-green-500 inline-block" />}
                                  {isEnd && <span title="Contract ends" className="w-2 h-2 rounded-full bg-red-500 inline-block" />}
                                  {isRenewal && <span title="Renewal due" className="w-2 h-2 rounded-full bg-amber-500 inline-block" />}
                                  {isReviewMonth && !isRenewal && <span title="Review deadline" className="w-2 h-2 rounded-full bg-orange-400 inline-block" />}
                                </div>
                                {/* Date labels */}
                                {isStart && <span className="text-[9px] text-green-700 font-semibold leading-tight">
                                  {new Date(sub.StartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                </span>}
                                {isEnd && <span className="text-[9px] text-red-700 font-semibold leading-tight">
                                  Ends {new Date(sub.EndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                </span>}
                                {isRenewal && <span className="text-[9px] text-amber-700 font-semibold leading-tight">
                                  Renews {new Date(sub.RenewalDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                </span>}
                                {/* Amount */}
                                {amtDisplay && (
                                  <span className={`font-mono text-[10px] font-semibold ${isEnd ? 'text-red-700' : isStart ? 'text-green-700' : isRenewal ? 'text-amber-700' : sub.PaymentFrequency === 'Monthly' ? 'text-blue-700' : 'text-purple-700'}`}>
                                    {amtDisplay}
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        })}

                        {/* FY Total */}
                        <td className="px-3 py-2 text-right font-mono font-bold border-l border-gray-200">
                          <span className={fyTotal > 0 ? 'text-gray-800' : 'text-gray-300'}>{fyTotal > 0 ? formatAmt(fyTotal) : '—'}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                {/* Totals footer */}
                <tfoot className="sticky bottom-0 z-20">
                  <tr className="bg-gray-800 text-white">
                    <td className="sticky left-0 z-30 bg-gray-800 px-3 py-2.5 font-semibold" colSpan={3}>Monthly totals</td>
                    <td className="bg-gray-700 px-2 py-2.5" />
                    <td className="bg-gray-700 px-2 py-2.5" />
                    {monthTotals.map((m, i) => (
                      <td key={i} className="px-2 py-2.5 text-right font-mono font-semibold border-l border-gray-700">
                        <div className={m.total > 0 ? 'text-blue-300' : 'text-gray-600'}>{m.total > 0 ? formatAmt(m.total) : '—'}</div>
                        {m.annualInMonth > 0 && <div className="text-purple-300 text-[10px]">+{formatAmt(m.annualInMonth)}</div>}
                      </td>
                    ))}
                    <td className="bg-gray-700 px-3 py-2.5 text-right font-mono font-bold border-l border-gray-700">
                      {formatAmt(filteredSubscriptions.filter(s => s.PaymentFrequency === 'Monthly').reduce((sum, s) => {
                        const activeMonths = fyMonths.filter(m => isActiveInMonth(s, m)).length;
                        return sum + activeMonths * (s.PaymentAmount || 0);
                      }, 0) + filteredSubscriptions.filter(s => s.PaymentFrequency === 'Annually').reduce((sum, s) => {
                        const hasPaymentInFY = fyMonths.some(m => { const { isStart, isRenewal } = getMonthMarkers(s, m); return isStart || isRenewal; });
                        return hasPaymentInFY ? sum + (s.PaymentAmount || 0) : sum;
                      }, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingId ? 'Edit Subscription' : 'New Subscription'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs — only shown when editing */}
            {editingId && (
              <div className="flex border-b bg-gray-50 px-6">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'details' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                  Contract Details
                </button>
                <button
                  onClick={() => setActiveTab('invoices')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'invoices' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                  <Link2 className="w-3.5 h-3.5" />
                  Linked Invoices
                  {linkedInvoices.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">{linkedInvoices.length}</span>
                  )}
                </button>
              </div>
            )}

            {/* Linked Invoices Tab */}
            {editingId && activeTab === 'invoices' && (
              <div className="p-6">
                {linkedInvoicesLoading ? (
                  <div className="flex items-center justify-center py-10 gap-2 text-gray-500">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                    Loading linked invoices...
                  </div>
                ) : linkedInvoices.length === 0 ? (
                  <div className="text-center py-10">
                    <Link2 className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                    <p className="text-gray-500 font-medium">No linked invoices</p>
                    <p className="text-gray-400 text-sm mt-1">Invoice lines can be linked to this contract during finance review.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Linked £</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {linkedInvoices.map(inv => (
                          <tr key={inv.InvoiceID} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 font-medium text-blue-700">{inv.InvoiceNumber || '—'}</td>
                            <td className="px-4 py-2.5 text-gray-600">{inv.InvoiceDate ? new Date(inv.InvoiceDate).toLocaleDateString() : '—'}</td>
                            <td className="px-4 py-2.5 text-right font-mono font-semibold text-gray-800">
                              {inv.LinkedAmount != null ? `£${Number(inv.LinkedAmount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                inv.Status === 'Approved' ? 'bg-green-100 text-green-800'
                                : inv.Status === 'Pending' ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-600'
                              }`}>{inv.Status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Details Tab (form) — shown when not editing, or when editing and tab = details */}
            {(!editingId || activeTab === 'details') && (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.contractName}
                    onChange={(e) => setFormData({ ...formData, contractName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vendor *
                  </label>
                  <select
                    required
                    value={formData.vendorId}
                    onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select vendor...</option>
                    {vendors.filter(v => v.IsActive === true || v.IsActive === 1).map(vendor => (
                      <option key={vendor.VendorID} value={vendor.VendorID}>
                        {vendor.VendorName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <select
                    value={formData.departmentId}
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No specific department</option>
                    {departments.map(dept => (
                      <option key={dept.DepartmentID} value={dept.DepartmentID}>
                        {dept.DepartmentName || dept.Name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Number
                  </label>
                  <input
                    type="text"
                    value={formData.contractNumber}
                    onChange={(e) => setFormData({ ...formData, contractNumber: e.target.value })}
                    placeholder="e.g. CNT-2026-001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="GBP">GBP — British Pound</option>
                    <option value="USD">USD — US Dollar</option>
                    <option value="EUR">EUR — Euro</option>
                    <option value="CAD">CAD — Canadian Dollar</option>
                    <option value="AUD">AUD — Australian Dollar</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Frequency *
                  </label>
                  <select
                    required
                    value={formData.paymentFrequency}
                    onChange={(e) => setFormData({ ...formData, paymentFrequency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Annually">Annually</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Amount *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.paymentAmount}
                    onChange={(e) => setFormData({ ...formData, paymentAmount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Day of Month
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.paymentDayOfMonth}
                    onChange={(e) => setFormData({ ...formData, paymentDayOfMonth: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GL Account
                    {formData.departmentId && <span className="ml-1 text-xs text-blue-500 font-normal">(filtered by department)</span>}
                  </label>
                  <select
                    value={formData.glAccountId}
                    onChange={(e) => setFormData({ ...formData, glAccountId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select GL account...</option>
                    {formGLAccounts.map(gl => (
                      <option key={gl.GLAccountID} value={gl.GLAccountID}>
                        {gl.AccountCode} - {gl.AccountName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Renewal Date
                  </label>
                  <input
                    type="date"
                    value={formData.renewalDate}
                    onChange={(e) => setFormData({ ...formData, renewalDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.autoRenew}
                      onChange={(e) => setFormData({ ...formData, autoRenew: e.target.checked })}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Auto-Renew</span>
                      <p className="text-sm text-gray-500">
                        Automatically add to department budgets when enabled
                      </p>
                    </div>
                  </label>
                </div>

                {/* Notice Period */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notice Period <span className="text-gray-400 font-normal">(how far in advance you must notify to cancel/not renew)</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={formData.noticePeriodDays}
                      onChange={(e) => setFormData({ ...formData, noticePeriodDays: e.target.value })}
                      placeholder="e.g. 30"
                      className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    <select
                      value={formData.noticePeriodUnit}
                      onChange={(e) => setFormData({ ...formData, noticePeriodUnit: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="Days">Days</option>
                      <option value="Weeks">Weeks</option>
                      <option value="Months">Months</option>
                    </select>
                  </div>
                </div>

                {/* Review By Date — computed read-only display */}
                {formData.renewalDate && formData.noticePeriodDays && (() => {
                  const rbd = computeReviewByDate(formData.renewalDate, formData.noticePeriodDays, formData.noticePeriodUnit);
                  const status = getReviewStatus(rbd);
                  if (!rbd || !status) return null;
                  return (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" /> Review By Date <span className="text-gray-400 font-normal">(calculated — renewal minus notice)</span>
                      </label>
                      <div className={`px-3 py-2.5 rounded-lg border text-sm font-medium flex items-center gap-2 ${status.color}`}>
                        <Clock className="w-4 h-4 flex-shrink-0" />
                        <span>{rbd.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                        <span className="ml-auto text-xs opacity-80">{status.label.includes('overdue') ? status.label : `${status.label} remaining`}</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Business rule callout */}
                {formData.noticePeriodDays && (
                  <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex gap-2">
                    <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-700">
                      <strong>Notice Period:</strong> You'll need to action this contract by the Review By Date above to avoid automatic renewal. The Review By column in the subscription list shows urgency with colour coding.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingId ? 'Update Subscription' : 'Create Subscription'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
            )} {/* end details tab */}
          </div>
        </div>
      )}
    </div>
  );
}
